import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Check, X, Crown, Loader2, Tag, Sparkles, CreditCard, CheckCircle2, AlertTriangle, RefreshCw, XCircle, Info } from 'lucide-react';

export default function PlanUpgradePage() {
  const { user, updateUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [planData, setPlanData] = useState(null);
  const [subInfo, setSubInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoCode, setPromoCode] = useState('');
  const [applying, setApplying] = useState(false);
  const [upgrading, setUpgrading] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [polling, setPolling] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(null);

  const fetchPlan = useCallback(async () => {
    try {
      const [planRes, subRes] = await Promise.all([
        api.get('/plan'),
        api.get('/subscription/info').catch(() => ({ data: null })),
      ]);
      setPlanData(planRes.data);
      setSubInfo(subRes.data);
    } catch {
      toast.error('Failed to load plan info');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  // Poll payment status when returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const planKey = searchParams.get('plan');
    if (sessionId) {
      setPolling(true);
      pollPaymentStatus(sessionId, planKey, 0);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, planKey, attempts) => {
    if (attempts >= 8) {
      setPolling(false);
      toast.error('Payment status check timed out. Please refresh the page.');
      setSearchParams({});
      return;
    }
    try {
      const res = await api.get(`/subscription/status/${sessionId}`);
      if (res.data.status === 'paid') {
        setPolling(false);
        setPaymentSuccess(res.data);
        toast.success(res.data.message);
        setSearchParams({});
        fetchPlan();
        const meRes = await api.get('/auth/me');
        updateUser(meRes.data);
        return;
      }
      if (res.data.status === 'expired') {
        setPolling(false);
        toast.error('Payment session expired.');
        setSearchParams({});
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, planKey, attempts + 1), 2000);
    } catch {
      setPolling(false);
      toast.error('Error checking payment status.');
      setSearchParams({});
    }
  };

  const handleUpgrade = async (planKey) => {
    setUpgrading(planKey);
    try {
      const res = await api.post('/subscription/checkout', { plan: planKey, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setUpgrading(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will keep access until the end of your current billing period.')) return;
    setCancelling(true);
    try {
      const res = await api.post('/subscription/cancel');
      toast.success(res.data.message);
      fetchPlan();
      const meRes = await api.get('/auth/me');
      updateUser(meRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  const applyPromo = async () => {
    if (!promoCode.trim()) return toast.error('Please enter a promo code');
    setApplying(true);
    try {
      const res = await api.post('/promo/apply', { code: promoCode.trim() });
      toast.success(res.data.message);
      setPromoCode('');
      fetchPlan();
      const meRes = await api.get('/auth/me');
      updateUser(meRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid promo code');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin" style={{ color: '#195259' }} /></div>;
  }

  const currentPlan = planData?.plan || 'free';
  const plans = planData?.plans || {};
  const planOrder = ['pro', 'premium'];
  const expiresAt = planData?.plan_expires_at;
  const isCancelling = subInfo?.cancel_at_period_end || subInfo?.subscription_status === 'cancelling';
  const hasActiveSub = subInfo?.stripe_subscription_id && subInfo?.subscription_status === 'active';

  return (
    <div className="space-y-8 max-w-4xl mx-auto" data-testid="plan-upgrade-page">
      {/* Payment Processing Overlay */}
      {polling && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <Card className="w-full max-w-sm"><CardContent className="p-8 text-center space-y-4">
            <Loader2 className="h-10 w-10 animate-spin mx-auto" style={{ color: '#FF9A00' }} />
            <h3 className="text-lg font-bold" style={{ color: '#195259' }}>Processing Payment</h3>
            <p className="text-sm" style={{ color: '#6D6E71' }}>Please wait while we confirm your payment...</p>
          </CardContent></Card>
        </div>
      )}

      {/* Payment Success Banner */}
      {paymentSuccess && (
        <Card style={{ border: '2px solid #22c55e' }}><CardContent className="p-5 flex items-center gap-3">
          <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
          <div>
            <p className="font-semibold text-green-700">{paymentSuccess.message}</p>
            {paymentSuccess.plan_expires_at && (
              <p className="text-xs text-green-600 mt-0.5">Next billing: {new Date(paymentSuccess.plan_expires_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            )}
          </div>
        </CardContent></Card>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ color: '#195259' }}>Your Plan</h1>
        <p className="text-sm mt-1" style={{ color: '#6D6E71' }}>Manage your subscription and upgrade to unlock more features.</p>
      </div>

      {/* Current Plan Banner */}
      <Card style={{ border: '2px solid #FF9A00' }}>
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FF9A00' }}>
                <Crown className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="font-semibold" style={{ color: '#195259' }}>
                  Currently on <span className="capitalize">{currentPlan}</span> plan
                </p>
                {expiresAt && (
                  <p className="text-xs" style={{ color: '#6D6E71' }}>
                    {isCancelling ? 'Access until' : 'Next billing'}: {new Date(expiresAt).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-1">
                  {planData?.promo_applied && (
                    <Badge className="text-xs" style={{ backgroundColor: '#195259', color: 'white' }}>Promo: {planData.promo_applied}</Badge>
                  )}
                  {hasActiveSub && !isCancelling && (
                    <Badge className="text-xs" style={{ backgroundColor: '#22c55e', color: 'white' }}>
                      <RefreshCw className="h-3 w-3 mr-1" /> Auto-renew active
                    </Badge>
                  )}
                  {isCancelling && (
                    <Badge className="text-xs" style={{ backgroundColor: '#f59e0b', color: 'white' }}>
                      <AlertTriangle className="h-3 w-3 mr-1" /> Cancelling at period end
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            {hasActiveSub && !isCancelling && (
              <Button variant="outline" size="sm" className="text-xs" style={{ borderColor: '#dc2626', color: '#dc2626' }} onClick={handleCancel} disabled={cancelling} data-testid="cancel-subscription-btn">
                {cancelling ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                Cancel Subscription
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Billing Notice — show for all users */}
      {!hasActiveSub && (
        <Card style={{ border: '1.5px solid #FF9A00' }}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#FF9A00' }} />
              <div className="text-sm" style={{ color: '#195259' }}>
                <p className="font-semibold mb-1">How subscription billing works:</p>
                <ul className="space-y-1 text-xs" style={{ color: '#6D6E71' }}>
                  <li>Your card will be charged <strong>automatically every month</strong> on the same date you subscribe.</li>
                  <li>You can <strong>cancel anytime</strong> from this page. You will keep access until the end of your billing period.</li>
                  <li>Upgrading from Pro to Premium will cancel your Pro subscription and start a new Premium one.</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl mx-auto">
        {planOrder.map((key) => {
          const plan = plans[key];
          if (!plan) return null;
          const isCurrent = currentPlan === key;
          const currentIdx = planOrder.indexOf(currentPlan);
          const planIdx = planOrder.indexOf(key);
          const isUpgrade = planIdx > currentIdx;
          const isProcessing = upgrading === key;

          return (
            <Card key={key} className={`relative transition-all duration-200 ${isCurrent ? 'shadow-lg' : 'hover:shadow-md'}`} style={isCurrent ? { border: '2px solid #FF9A00' } : {}} data-testid={`plan-card-${key}`}>
              {isCurrent && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="px-3 text-white text-xs font-semibold" style={{ backgroundColor: '#FF9A00' }}>Current Plan</Badge>
                </div>
              )}
              <CardContent className="p-6 pt-7">
                <h3 className="text-lg font-bold" style={{ color: '#195259' }}>{plan.name}</h3>
                <div className="my-4">
                  <span className="text-3xl font-extrabold" style={{ color: '#195259' }}>RM {plan.price}</span>
                  <span className="text-sm" style={{ color: '#6D6E71' }}>{plan.period}</span>
                </div>
                <div className="space-y-2.5 mb-6">
                  {plan.features.map((f) => (
                    <div key={f.text} className="flex items-center gap-2 text-sm">
                      {f.on ? <Check className="h-4 w-4 shrink-0" style={{ color: '#FF9A00' }} /> : <X className="h-4 w-4 shrink-0 text-gray-300" />}
                      <span style={{ color: f.on ? '#195259' : '#B0B0B0' }}>{f.text}</span>
                    </div>
                  ))}
                </div>
                {isCurrent ? (
                  <Button disabled className="w-full h-10 rounded-xl font-semibold opacity-60" style={{ backgroundColor: 'rgba(25,82,89,0.1)', color: '#195259' }}>Current Plan</Button>
                ) : isUpgrade ? (
                  <Button className="w-full h-10 rounded-xl font-semibold text-white" style={{ backgroundColor: '#195259' }} onClick={() => handleUpgrade(key)} disabled={isProcessing || !!upgrading} data-testid={`upgrade-${key}-btn`}>
                    {isProcessing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</> : <><CreditCard className="h-4 w-4 mr-2" /> Upgrade to {plan.name}</>}
                  </Button>
                ) : (
                  <Button variant="outline" className="w-full h-10 rounded-xl font-semibold" style={{ borderColor: '#195259', color: '#195259' }}>Downgrade</Button>
                )}
                {key !== 'free' && isUpgrade && (
                  <p className="text-[10px] text-center mt-2" style={{ color: '#6D6E71' }}>Auto-renews monthly. Cancel anytime.</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Promo Code Section */}
      <Card data-testid="promo-code-section">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="h-5 w-5" style={{ color: '#FF9A00' }} />
            <h2 className="text-lg font-bold" style={{ color: '#195259' }}>Promo Code</h2>
          </div>
          <p className="text-sm mb-4" style={{ color: '#6D6E71' }}>Have a promo code? Enter it below to unlock special access.</p>
          <div className="flex gap-3">
            <Input data-testid="promo-code-input" placeholder="Enter promo code" value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === 'Enter' && applyPromo()} className="max-w-xs uppercase font-mono tracking-wider" style={{ borderColor: '#195259' }} />
            <Button data-testid="apply-promo-btn" onClick={applyPromo} disabled={applying || !promoCode.trim()} className="font-semibold text-white px-6" style={{ backgroundColor: '#195259' }}>
              {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1.5" /> Apply</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
